import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { UtensilsCrossed, ImageOff, AlertTriangle } from 'lucide-react';
import API_URL from '../../config/api';

// ⭐ Menú Web Estático (público, sin login): esto es lo que ve un comensal
// al escanear el QR de la mesa. v1 usa una plantilla ya diseñada (el dueño
// solo elige cuál + logo/colores/portada/tagline, ver MenuWebManager.js) en
// vez de un editor libre -- ver decisión en la conversación de diseño.
// Los datos vienen de GET /api/menu/public/:slug (sin auth), que ya filtra
// productos inactivos y sin stock -- acá no hay que volver a filtrar nada,
// solo agrupar por categoría para pintar.

const ALL_CATEGORY = 'Todas';

function groupByCategory(products) {
  const map = {};
  products.forEach((p) => {
    const cat = p.category || 'Otros';
    (map[cat] = map[cat] || []).push(p);
  });
  return map;
}

function ProductImage({ src, alt, className }) {
  if (!src) {
    return (
      <div className={`${className} bg-gray-100 flex items-center justify-center`}>
        <ImageOff className="w-6 h-6 text-gray-300" />
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={(e) => { e.target.style.display = 'none'; }} />;
}

function LoadingScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-4 border-gray-400 mx-auto mb-4" />
        <p className="text-gray-500">Cargando menú...</p>
      </div>
    </div>
  );
}

function NotFoundScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
      <div className="text-center max-w-sm">
        <AlertTriangle className="w-14 h-14 text-gray-300 mx-auto mb-4" />
        <h1 className="text-xl font-bold text-gray-700 mb-1">Este menú no está disponible</h1>
        <p className="text-sm text-gray-500">Verifica el enlace o pregunta al negocio si su menú web sigue activo.</p>
      </div>
    </div>
  );
}

function CategoryTabs({ categories, active, onSelect, activeClass, inactiveClass, wrapClass, activeStyle }) {
  if (categories.length <= 1) return null;
  return (
    <div className={wrapClass}>
      <button
        onClick={() => onSelect(ALL_CATEGORY)}
        className={active === ALL_CATEGORY ? activeClass : inactiveClass}
        style={active === ALL_CATEGORY ? activeStyle : undefined}
      >
        {ALL_CATEGORY}
      </button>
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => onSelect(cat)}
          className={active === cat ? activeClass : inactiveClass}
          style={active === cat ? activeStyle : undefined}
        >
          {cat}
        </button>
      ))}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Plantilla "Elegante": fondo oscuro, acentos dorados/ámbar, tipografía
// serif, lista clásica de restaurante fino en vez de tarjetas.
// ──────────────────────────────────────────────────────────────────────────
function TemplateElegante({ menu, categories, active, setActive, products }) {
  const primary = menu.primary_color;
  const secondary = menu.secondary_color;
  return (
    <div className="min-h-screen bg-gray-950 text-gray-100 pb-16">
      <div className="relative h-56 sm:h-72 w-full overflow-hidden">
        {menu.cover_url ? (
          <img src={menu.cover_url} alt="" className="w-full h-full object-cover opacity-60" />
        ) : (
          <div className="w-full h-full" style={{ background: `linear-gradient(135deg, ${primary}, #000)` }} />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-gray-950 via-gray-950/60 to-transparent" />
        {menu.logo_url && (
          <img
            src={menu.logo_url}
            alt={menu.business_name}
            className="absolute left-1/2 -translate-x-1/2 bottom-[-2.5rem] w-20 h-20 rounded-full border-4 object-cover bg-gray-900 shadow-xl"
            style={{ borderColor: secondary }}
          />
        )}
      </div>

      <div className="text-center mt-14 px-4">
        <h1 className="font-serif text-2xl sm:text-3xl font-bold tracking-wide">{menu.business_name}</h1>
        {menu.tagline && <p className="text-sm text-gray-400 italic mt-1">{menu.tagline}</p>}
        <div className="w-16 h-px mx-auto mt-4" style={{ backgroundColor: secondary }} />
      </div>

      <CategoryTabs
        categories={categories}
        active={active}
        onSelect={setActive}
        wrapClass="flex gap-2 overflow-x-auto px-4 mt-6 pb-1 justify-center flex-wrap"
        activeClass="px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap"
        activeStyle={{ backgroundColor: secondary, color: '#111827' }}
        inactiveClass="px-4 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap bg-gray-900 text-gray-400 border border-gray-800"
      />

      <div className="max-w-2xl mx-auto mt-8 px-5 space-y-8">
        {(active === ALL_CATEGORY ? categories : [active]).map((cat) => {
          const items = products.filter((p) => (p.category || 'Otros') === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat}>
              <h2 className="font-serif text-lg font-bold mb-3 flex items-center gap-3" style={{ color: secondary }}>
                <span className="flex-1 h-px bg-gray-800" />
                {cat}
                <span className="flex-1 h-px bg-gray-800" />
              </h2>
              <div className="divide-y divide-gray-800">
                {items.map((p) => (
                  <div key={p.id} className="py-3 flex justify-between items-start gap-4">
                    <div className="flex-1">
                      <p className="font-medium text-gray-100">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-500 mt-0.5">{p.description}</p>}
                    </div>
                    <p className="font-serif font-bold whitespace-nowrap" style={{ color: secondary }}>
                      ${Number(p.price).toFixed(2)}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <p className="text-center text-gray-500 text-sm py-10">Todavía no hay productos disponibles en esta categoría.</p>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Plantilla "Casual": vibrante, tarjetas grandes con foto, pestañas de
// categoría redondeadas — pensada para comida rápida / ambiente relajado.
// ──────────────────────────────────────────────────────────────────────────
function TemplateCasual({ menu, categories, active, setActive, products }) {
  const primary = menu.primary_color;
  const secondary = menu.secondary_color;
  return (
    <div className="min-h-screen bg-gray-50 pb-16">
      <div className="relative h-44 sm:h-56 w-full overflow-hidden rounded-b-[2.5rem]" style={{ backgroundColor: primary }}>
        {menu.cover_url && <img src={menu.cover_url} alt="" className="w-full h-full object-cover opacity-90" />}
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4" style={{ background: `${primary}99` }}>
          {menu.logo_url && (
            <img src={menu.logo_url} alt={menu.business_name} className="w-16 h-16 rounded-2xl object-cover shadow-lg mb-2 bg-white" />
          )}
          <h1 className="text-white text-2xl font-extrabold drop-shadow">{menu.business_name}</h1>
          {menu.tagline && <p className="text-white/90 text-sm mt-1">{menu.tagline}</p>}
        </div>
      </div>

      <CategoryTabs
        categories={categories}
        active={active}
        onSelect={setActive}
        wrapClass="flex gap-2 overflow-x-auto px-4 mt-5 pb-1"
        activeClass="px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap text-white shadow-md"
        activeStyle={{ backgroundColor: primary }}
        inactiveClass="px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap bg-white text-gray-500 border border-gray-200"
      />

      <div className="max-w-3xl mx-auto mt-6 px-4">
        {(active === ALL_CATEGORY ? categories : [active]).map((cat) => {
          const items = products.filter((p) => (p.category || 'Otros') === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} className="mb-7">
              <h2 className="text-lg font-extrabold mb-3" style={{ color: primary }}>{cat}</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {items.map((p) => (
                  <div key={p.id} className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-100">
                    <ProductImage src={p.image_url} alt={p.name} className="w-full h-24 object-cover" />
                    <div className="p-3">
                      <p className="font-bold text-gray-800 text-sm leading-tight line-clamp-2">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-400 mt-0.5 line-clamp-2">{p.description}</p>}
                      <p className="mt-1.5 font-extrabold text-base" style={{ color: secondary }}>${Number(p.price).toFixed(2)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-10">Todavía no hay productos disponibles en esta categoría.</p>
        )}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// Plantilla "Minimalista": mucho blanco, tipografía fina, un solo color de
// acento para precios, sin ruido visual.
// ──────────────────────────────────────────────────────────────────────────
function TemplateMinimalista({ menu, categories, active, setActive, products }) {
  const primary = menu.primary_color;
  return (
    <div className="min-h-screen bg-white pb-16">
      <div className="max-w-xl mx-auto pt-10 px-6 text-center">
        {menu.logo_url && (
          <img src={menu.logo_url} alt={menu.business_name} className="w-16 h-16 rounded-full object-cover mx-auto mb-4 border border-gray-100" />
        )}
        <h1 className="text-2xl font-light tracking-wide text-gray-900">{menu.business_name}</h1>
        {menu.tagline && <p className="text-sm text-gray-400 mt-1 font-light">{menu.tagline}</p>}
      </div>

      <CategoryTabs
        categories={categories}
        active={active}
        onSelect={setActive}
        wrapClass="flex gap-4 overflow-x-auto px-6 mt-8 pb-1 justify-center border-b border-gray-100"
        activeClass="pb-2 text-sm font-semibold whitespace-nowrap border-b-2"
        activeStyle={{ borderColor: primary, color: primary }}
        inactiveClass="pb-2 text-sm font-light whitespace-nowrap text-gray-400 border-b-2 border-transparent"
      />

      <div className="max-w-xl mx-auto mt-8 px-6">
        {(active === ALL_CATEGORY ? categories : [active]).map((cat) => {
          const items = products.filter((p) => (p.category || 'Otros') === cat);
          if (items.length === 0) return null;
          return (
            <div key={cat} className="mb-8">
              <h2 className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-3">{cat}</h2>
              <div className="space-y-4">
                {items.map((p) => (
                  <div key={p.id} className="flex justify-between items-baseline gap-3">
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-900 font-light">{p.name}</p>
                      {p.description && <p className="text-xs text-gray-400 font-light mt-0.5">{p.description}</p>}
                    </div>
                    <span className="flex-1 border-b border-dotted border-gray-200 mb-1.5" />
                    <p className="font-semibold whitespace-nowrap" style={{ color: primary }}>${Number(p.price).toFixed(2)}</p>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
        {products.length === 0 && (
          <p className="text-center text-gray-400 text-sm py-10 font-light">Todavía no hay productos disponibles en esta categoría.</p>
        )}
      </div>
    </div>
  );
}

const TEMPLATES = { elegante: TemplateElegante, casual: TemplateCasual, minimalista: TemplateMinimalista };

export default function MenuPublico({ slug }) {
  const [menu, setMenu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(ALL_CATEGORY);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    axios.get(`${API_URL}/api/menu/public/${encodeURIComponent(slug)}`)
      .then((res) => { if (!cancelled) setMenu(res.data); })
      .catch(() => { if (!cancelled) setNotFound(true); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Título de la pestaña del navegador: mejor "La Sazón de Oso" que el
  // nombre genérico de la app cuando alguien comparte/guarda este enlace.
  useEffect(() => {
    if (menu?.business_name) document.title = `${menu.business_name} — Menú`;
  }, [menu]);

  const productsByCategory = useMemo(() => (menu ? groupByCategory(menu.products) : {}), [menu]);
  const categoriesWithItems = useMemo(
    () => (menu ? menu.categories.filter((c) => (productsByCategory[c] || []).length > 0) : []),
    [menu, productsByCategory]
  );

  if (loading) return <LoadingScreen />;
  if (notFound || !menu) return <NotFoundScreen />;

  const Template = TEMPLATES[menu.template] || TemplateElegante;

  return (
    <>
      <Template
        menu={menu}
        categories={categoriesWithItems}
        active={active}
        setActive={setActive}
        products={menu.products}
      />
      <footer className="fixed bottom-0 left-0 right-0 bg-black/80 text-white text-[11px] text-center py-1.5 flex items-center justify-center gap-1">
        <UtensilsCrossed className="w-3 h-3" /> Menú digital creado con Mi POS Fácil
      </footer>
    </>
  );
}
